import {
  Directive,
  ElementRef,
  Renderer2,
  inject,
  input,
  output,
  signal
} from '@angular/core';

/**
 * Clases que se aplican mientras se arrastra un archivo sobre la zona.
 * Se usa ring (no border) para no competir con el borde base del contenedor.
 */
const CLASES_ACTIVA = [
  'ring-2',
  'ring-blue-500',
  'bg-blue-50',
  'dark:ring-blue-400',
  'dark:bg-blue-900/30'
];

/**
 * DIRECTIVA: appDropZone
 *
 * Permite soltar un archivo sobre un contenedor. Sólo entrega el archivo: la
 * validación (tipo, tamaño, cantidad) sigue en el mismo método que usa la
 * pantalla para la selección por clic.
 *
 * Legacy: gwtupload MultiUploader(FileInputType.BROWSER_INPUT) con
 * setMultipleSelection(false) — el input nativo aceptaba arrastrar un archivo.
 * Por eso si se sueltan varios se entrega sólo el primero.
 *
 * @example
 * <div appDropZone (archivoSoltado)="procesarArchivo($event)"
 *      [dropZoneDisabled]="cargando()">...</div>
 */
@Directive({
  selector: '[appDropZone]',
  standalone: true,
  exportAs: 'appDropZone',
  host: {
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)',
    '(drop)': 'onDrop($event)'
  }
})
export class DropZoneDirective {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);

  dropZoneDisabled = input<boolean>(false);
  archivoSoltado = output<File>();

  readonly activo = signal(false);

  onDragOver(event: DragEvent): void {
    // Sin preventDefault el navegador abre el archivo y se pierde el formulario
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.dropZoneDisabled() ? 'none' : 'copy';
    }
    this.setActivo(!this.dropZoneDisabled());
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Ignorar el dragleave que se dispara al pasar sobre elementos hijos
    const destino = event.relatedTarget as Node | null;
    if (destino && this.el.nativeElement.contains(destino)) return;
    this.setActivo(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.setActivo(false);
    if (this.dropZoneDisabled()) return;
    const archivo = event.dataTransfer?.files?.[0];
    if (archivo) this.archivoSoltado.emit(archivo);
  }

  private setActivo(valor: boolean): void {
    if (this.activo() === valor) return;
    this.activo.set(valor);
    const host = this.el.nativeElement;
    for (const clase of CLASES_ACTIVA) {
      if (valor) this.renderer.addClass(host, clase);
      else this.renderer.removeClass(host, clase);
    }
  }
}
